/**
 * Which of a submission's model runs were superseded — written by an analysis
 * attempt whose result is not the one recorded.
 *
 * An analysis job attempt the dispatcher timed out used to run on and save its
 * outputs anyway, primary flag and all, beside the retry's. One submission then
 * had two primary outputs, and a reader that took the first showed the abandoned
 * run's 10/10 on an attempt recorded at 5 (prod, 2026-09-24).
 *
 * Rows are grouped into the save that wrote them: by `jobAttempt` where the row
 * carries one, else by `generatedAt`, which is the save time and is shared by
 * every row one run saved. A group is superseded when it holds an `abandoned`
 * row, or a primary other than the newest one. The newest primary that is not
 * abandoned is the run whose scores were recorded — the backend reads it the
 * same way (`newestPrimaryPerInput`).
 */

export interface RunForSupersession {
    id: string;
    isPrimary: boolean;
    generatedAt: string;
    jobAttempt?: number;
    abandoned?: boolean;
}

export interface Supersession {
    /** The primary output whose result was recorded, if any. */
    activePrimaryId?: string;
    /** Why a row is superseded; absent for rows that are not. */
    reasonFor(row: RunForSupersession): SupersededReason | undefined;
}

export type SupersededReason =
    /** Stamped by the backend: the job runner had given up on this attempt. */
    | { kind: 'abandoned'; jobAttempt?: number }
    /** Inferred for rows written before `abandoned` existed: a primary lost to a newer one. */
    | { kind: 'superseded'; jobAttempt?: number };

const saveKey = (r: RunForSupersession) =>
    r.jobAttempt !== undefined ? `attempt:${r.jobAttempt}` : `saved:${r.generatedAt}`;

export function classifySupersededRuns(rows: RunForSupersession[]): Supersession {
    const activePrimary = rows
        .filter(r => r.isPrimary && !r.abandoned)
        .reduce<RunForSupersession | undefined>(
            (best, r) => (!best || r.generatedAt > best.generatedAt ? r : best), undefined);

    const reasons = new Map<string, SupersededReason>();
    for (const r of rows) {
        if (r.abandoned) reasons.set(saveKey(r), { kind: 'abandoned', jobAttempt: r.jobAttempt });
    }
    for (const r of rows) {
        const key = saveKey(r);
        if (r.isPrimary && r.id !== activePrimary?.id && !reasons.has(key)) {
            reasons.set(key, { kind: 'superseded', jobAttempt: r.jobAttempt });
        }
    }

    return {
        activePrimaryId: activePrimary?.id,
        reasonFor: row => reasons.get(saveKey(row)),
    };
}
